import { store } from '@redux/store';
import { filter, fromEvent, map } from 'rxjs';
import ModalStep from '../enums/modalStep';
import { setModalStep } from '../redux/slices';
import createUserIdentificationVerificationInfo$ from '../apis/createUserIdentificationVerificationInfo';
import { getStoredUserData } from '../services/userService';
import {
  idCardSvgElement,
  creditCardSvgElement,
  jumioLogoSvgElement,
  stripeLogoSvgElement,
} from '../constants/svgIcons';
import VerificationMethod from '../enums/verificationMethod';
import UserStatus from '../enums/userStatus';

export default class IdentityVerification {
  ccButtonSubscription$ = null;

  constructor(rootComponent) {
    const { specId } = rootComponent.getSpecDetails();
    this.rootComponent = rootComponent;
    this.iframeVerificationStep = ModalStep.IframeVerification;
    this.specId = specId;
    this.presentErrorState = false;
    this.containerSelector = '[data-identify-verification]';
    this.ccButtonSelector = '[data-self-guided-tour-verify-cc-button]';
    this.identificationVerificationId = null;
    this.hasCreditCardVerification = this.rootComponent.verificationMethods.includes(VerificationMethod.CreditCard);
    this.hasIdentification = this.rootComponent.verificationMethods.includes(VerificationMethod.Identification);
    this.rootComponent.subscribe('domUpdate', this.onDomUpdate.bind(this));
    this.rootComponent.presentErrorState$.subscribe(this.setPresentErrorState.bind(this));
  }

  getCCVerificationButton() {
    return this.getContainer().querySelector(this.ccButtonSelector);
  }

  /**
   * Handles the DOM update event for the identity verification component.
   *
   * @param {CustomEvent} event - The DOM update event object.
   * @param {Object} event.detail - The detail object of the event.
   * @param {string} event.detail.step - The current step in the modal process.
   */
  onDomUpdate(event) {
    if (event.detail && event.detail.step !== ModalStep.IdentityVerification) {
      this.ccButtonSubscription$?.unsubscribe();
      this.ccButtonSubscription$ = null;
      return;
    }
    this.setUpEvents();
    if (this.presentErrorState) {
      this.showErrorMessage('Error verifying the user. Please try again.');
      this.rootComponent.presentErrorState$.next(false);
    }
  }

  /**
   * Retrieves the container element based on the specified selector.
   *
   * @returns {Element|null} The DOM element matching the container selector, or null if no match is found.
   */
  getContainer() {
    return document.querySelector(this.containerSelector);
  }

  /**
   * Sets the error state for the component.
   *
   * @param {boolean} presentErrorState - A boolean indicating whether the error state should be present.
   */
  setPresentErrorState(presentErrorState) {
    this.presentErrorState = presentErrorState;
  }

  /**
   * Sets up event listeners for user identification type selection.
   *
   * This method listens for click events on the container element and identifies
   * the closest element with the `data-user-identification-type` attribute. It then
   * extracts the value of the `data-user-identification-type` attribute and triggers
   * the `createUserIdentificationVerification` method with the extracted value.
   *
   * @returns {void}
   */
  setUpEvents() {
    const onClickIdentificationType$ = fromEvent(this.getContainer(), 'click').pipe(
      map((event) => event.target.closest('[data-user-identification-type]')),
      filter(Boolean),
      map((element) => element.dataset.userIdentificationType)
    );

    onClickIdentificationType$.subscribe(this.createUserIdentificationVerification.bind(this));

    const ccVerificationButton = this.getCCVerificationButton();
    if (ccVerificationButton) {
      this.ccButtonSubscription$ = fromEvent(ccVerificationButton, 'click').subscribe(() => {
        store.dispatch(setModalStep({ modalStep: ModalStep.CreditCardVerification }));
      });
    }
  }

  /**
   * Creates a user identification verification process based on the provided identification type.
   * This method initializes the verification process by creating user identification verification
   * information and setting up the necessary components for the verification flow.
   *
   * @param {string} identificationType - The type of identification to be used for verification.
   */
  createUserIdentificationVerification(identificationType) {
    const onCreateUserIdentification$ = createUserIdentificationVerificationInfo$({
      email: getStoredUserData().email,
      identificationType,
      specId: this.specId,
    });

    onCreateUserIdentification$.subscribe((data) => {
      this.setUpVerificationInfo(data);
      this.createIframeUtourVerification(data);
    });
  }

  /**
   * Sets up the verification information for the root component.
   *
   * @param {Object} data - The verification data to be assigned.
   */
  setUpVerificationInfo(data) {
    this.rootComponent.verificationInfo = data;
  }

  /**
   * Triggers the creation of an iframe for the Utour verification process.
   * Dispatches an action to update the modal step in the application state.
   */
  createIframeUtourVerification() {
    store.dispatch(setModalStep({ modalStep: this.iframeVerificationStep }));
  }

  /**
   * Handles the secondary action by dispatching an action to update the modal step
   * to the previous step in the application's state.
   */
  handleSecondaryAction() {
    const { name } = this.rootComponent.userStatus;
    const userStatus = UserStatus.fromKey(name);
    if (userStatus === UserStatus.NotFound) {
      store.dispatch(setModalStep({ modalStep: ModalStep.SignUp }));
      return;
    }
    store.dispatch(setModalStep({ modalStep: ModalStep.SignIn }));
  }

  /**
   * Displays an error message as a notification.
   *
   * @param {string} message - The error message to be displayed.
   */
  showErrorMessage(message) {
    this.rootComponent.showNotification(message);
  }

  /**
   * Renders the identification widget if the user has identification available.
   *
   * The widget includes options for verifying the user's identity using a driver's license,
   * identity card, or passport. It also displays relevant SVG elements and text instructions.
   *
   * @returns {string} The HTML string for the identification widget if `hasIdentification` is true,
   *                   otherwise an empty string.
   */
  renderIdentificationWidget() {
    return this.hasIdentification
      ? `
      <div class="nhs-c-self-guided-verification__wrapper-column nhs-u-min-width--2500">
        <div class="nhs-o-flex-column-items-center-self-stretch nhs-u-gap--640">
          ${idCardSvgElement}

          <p class="nhs-o-typography--souvenir-subhead-1-large-light nhs-u-text--align-center nhs-u-text--black-1000">Your Government-issued ID with your photo</p>
        </div>

        <div class="nhs-o-flex-column-items-center-self-stretch nhs-u-gap--300">
          <button type="button" class="nhs-c-button--secondary nhs-c-button--small-32 nhs-c-button--text-only" data-user-identification-type="DrivingLicense">
            Verify with your driver's license
          </button>

          <button type="button" class="nhs-c-button--secondary nhs-c-button--small-32 nhs-c-button--text-only" data-user-identification-type="IdCard">
            Verify with your identity card
          </button>

          <button type="button" class="nhs-c-button--secondary nhs-c-button--small-32 nhs-c-button--text-only" data-user-identification-type="Passport">
            Verify with your passport
          </button>

          ${jumioLogoSvgElement}
        </div>
      </div>
    `
      : '';
  }

  /**
   * Renders the credit card verification widget as an HTML string.
   *
   * This widget includes a description of the verification process,
   * a button to verify with a card, and associated SVG elements for
   * visual representation (e.g., credit card and Stripe logo).
   *
   * @returns {string} The HTML string for the credit card verification widget
   *                   if `hasCreditCardVerification` is true; otherwise, an empty string.
   */
  renderCreditCardWidget() {
    return this.hasCreditCardVerification
      ? `
      <div class="nhs-c-self-guided-verification__wrapper-column">
        <div class="nhs-o-flex-column-items-center-self-stretch nhs-u-gap--640">
          ${creditCardSvgElement}
          <div>
                <p class="nhs-o-typography--souvenir-subhead-1-large-light nhs-u-text--align-center nhs-u-margin--bottom-200 nhs-u-text--black-1000">Your credit or debit card</p>
                <p class="nhs-o-typography--sans-body-1-small-regular nhs-u-text--align-center nhs-u-text--black-900">You may see a temporary charge for identity verification that will be refunded automatically. UTour does not save your card information.</p>
          </div>
        </div>
        <div class="nhs-o-flex-column-items-center-self-stretch nhs-u-gap--300">
          <button type="button" class="nhs-c-button--secondary nhs-c-button--small-32 nhs-c-button--text-only" data-self-guided-tour-verify-cc-button>
            Verify with card
          </button>
          ${stripeLogoSvgElement}
        </div>
      </div>
    `
      : '';
  }

  /**
   * Renders the HTML structure for the identity verification component.
   *
   * @returns {string} The HTML string representing the identity verification component.
   */
  render() {
    return `
      <div class="nhs-c-self-guided-left-panel ${this.class}" data-identify-verification>
        <div>
          <h1 class="nhs-c-heading--self-guided-tour nhs-mq-heading--self-guided-tour-large">Verify identification</h1>

          <div class="nhs-c-notification__placeholder" data-self-guided-tour-notifications></div>

          <p class="nhs-c-label--black-900-body-1-medium-regular">Please select which method you prefer to verify your identity.</p>
        </div>

        <div class="nhs-c-self-guided-verification__wrapper nhs-mq-self-guided-verification__wrapper--extra-large">

          ${this.renderIdentificationWidget()}
          ${this.renderCreditCardWidget()}
        </div>

      </div>
    `;
  }
}
